import { useState, useEffect, useRef, useCallback } from "react";
import { API } from "../App";
import { Input } from "./ui/input";
import { MapPin, Loader2, CheckCircle } from "lucide-react";
import { cn } from "../lib/utils";

/**
 * NZ Address Autocomplete Component
 * Uses OpenStreetMap Nominatim API via backend proxy (FREE - no API key required)
 * Falls back to manual entry if API is unavailable
 */
export default function AddressAutocomplete({ 
  value, 
  onChange, 
  placeholder = "Start typing your address...",
  required = false,
  disabled = false,
  className = ""
}) {
  const [query, setQuery] = useState(value?.full_address || "");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(value || null);
  const [apiAvailable, setApiAvailable] = useState(true);
  const debounceTimer = useRef(null);
  const wrapperRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Update query when value changes externally
  useEffect(() => {
    if (value?.full_address && value.full_address !== query) {
      setQuery(value.full_address);
      setSelectedAddress(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const searchAddresses = useCallback(async (searchQuery) => {
    if (searchQuery.length < 3) {
      setSuggestions([]);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/address/autocomplete?q=${encodeURIComponent(searchQuery)}`);
      
      if (!response.ok) {
        throw new Error("API not available");
      }
      
      const data = await response.json();
      
      if (data.completions && data.completions.length > 0) {
        setSuggestions(data.completions);
        setShowDropdown(true);
        setApiAvailable(true);
      } else {
        setSuggestions([]);
      }
    } catch (error) {
      setApiAvailable(false);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounced search
  const handleInputChange = (e) => {
    const newQuery = e.target.value;
    setQuery(newQuery);
    setSelectedAddress(null);
    
    // If API not available, allow manual entry
    if (!apiAvailable) {
      onChange({
        full_address: newQuery,
        street_number: null,
        street_name: null,
        suburb: null,
        city: null,
        postcode: null,
        region: null,
        latitude: null,
        longitude: null
      });
      return;
    }

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      searchAddresses(newQuery);
    }, 800); // Longer debounce to respect Nominatim rate limits (1 req/sec)
  };

  const handleSelectAddress = async (suggestion) => {
    setLoading(true);
    try {
      // Nominatim returns lat/lon directly in autocomplete response
      const addressDetails = suggestion.address_details || {};
      
      const structuredAddress = {
        full_address: suggestion.a,
        street_number: addressDetails.house_number || null,
        street_name: addressDetails.road || null,
        suburb: addressDetails.suburb || null,
        city: addressDetails.city || addressDetails.town || addressDetails.village || null,
        postcode: addressDetails.postcode || null,
        region: addressDetails.state || addressDetails.region || null,
        latitude: suggestion.lat ? parseFloat(suggestion.lat) : null,
        longitude: suggestion.lon ? parseFloat(suggestion.lon) : null
      };
      
      setQuery(structuredAddress.full_address);
      setSelectedAddress(structuredAddress);
      setShowDropdown(false);
      setSuggestions([]);
      onChange(structuredAddress);
    } catch (error) {
      // Fallback to basic info from suggestion
      const basicAddress = {
        full_address: suggestion.a,
        street_number: null,
        street_name: null,
        suburb: null,
        city: null,
        postcode: null,
        region: null,
        latitude: suggestion.lat ? parseFloat(suggestion.lat) : null,
        longitude: suggestion.lon ? parseFloat(suggestion.lon) : null
      };
      
      setQuery(suggestion.a);
      setSelectedAddress(basicAddress);
      setShowDropdown(false);
      setSuggestions([]);
      onChange(basicAddress);
    } finally {
      setLoading(false);
    }
  };

  const handleFocus = () => {
    if (suggestions.length > 0 && apiAvailable) {
      setShowDropdown(true);
    }
  };

  return (
    <div ref={wrapperRef} className={cn("relative", className)}>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          type="text"
          placeholder={placeholder}
          value={query}
          onChange={handleInputChange}
          onFocus={handleFocus}
          required={required}
          disabled={disabled}
          className={cn(
            "pl-10 pr-10",
            selectedAddress && "border-green-500/50"
          )}
          data-testid="address-autocomplete-input"
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          {!loading && selectedAddress && (
            <CheckCircle className="h-4 w-4 text-green-500" />
          )}
        </div>
      </div>

      {/* Suggestions Dropdown */}
      {showDropdown && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-md shadow-lg max-h-60 overflow-auto">
          {suggestions.map((suggestion, index) => (
            <button
              key={suggestion.pxid || index}
              type="button"
              className="w-full px-4 py-3 text-left hover:bg-muted transition-colors border-b border-border last:border-b-0 flex items-start gap-3"
              onClick={() => handleSelectAddress(suggestion)}
              data-testid={`address-suggestion-${index}`}
            >
              <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0 text-muted-foreground" />
              <span className="text-sm">{suggestion.a}</span>
            </button>
          ))}
        </div>
      )}

      {/* Manual Entry Notice */}
      {!apiAvailable && query.length > 0 && (
        <p className="text-xs text-muted-foreground mt-1">
          Address lookup unavailable. Please enter your full address manually.
        </p>
      )}

      {/* Selected Address Details */}
      {selectedAddress && (selectedAddress.postcode || selectedAddress.suburb || selectedAddress.city) && (
        <div className="flex flex-wrap gap-2 mt-2">
          {selectedAddress.suburb && (
            <span className="text-xs bg-muted px-2 py-1 rounded">{selectedAddress.suburb}</span>
          )}
          {selectedAddress.city && (
            <span className="text-xs bg-muted px-2 py-1 rounded">{selectedAddress.city}</span>
          )}
          {selectedAddress.postcode && (
            <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">{selectedAddress.postcode}</span>
          )}
          {selectedAddress.latitude && selectedAddress.longitude && (
            <span className="text-xs bg-green-500/10 text-green-600 px-2 py-1 rounded">
              Location verified
            </span>
          )}
        </div>
      )}
    </div>
  );
}
